import { useRef, useState, useEffect, type MouseEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Form, Field } from "react-final-form";
import { useDispatch } from "react-redux";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";

import { httpPost, useHttpGets } from "../services/httpService";
import { ApiEndPoints } from "../utils/endpoints";
import {
  COUPON_CODES,
  IS_COUPONCODE_APPLIED,
  IS_MONTHLY_CASUAL_PLAN,
  MEMBER_KEY,
  MONTHLY_CASUAL_PLAN,
  TOKEN_KEY,
  USER_DETAIL,
  USER_ROLES,
  httpCode,
} from "../utils/constants";
import { setUserRoles } from "../utils/userRoles";

import { loginSuccess } from "../store/slice/authSlice";

import REICard from "../components/ui/card";
import REILinkButton from "../components/ui/linkButton";
import InputField from "../components/ui/inputField";
import ErrorMessage from "../components/ui/errorMessage";
import { REIProgressSpinner } from "../components/ui";

const Login = () => {
  const navigate = useNavigate();
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotEmailError, setForgotEmailError] = useState("");
  const [requestingPassword, setRequestingPassword] = useState(false);
  const dispatch = useDispatch();
  const searchParams = useSearchParams()[0];
  const redirectParam = searchParams.get("redirectTo");
  const sanitizeRedirect = (param: string | null): string => {
    if (!param) return "/";
    try {
      const resolved = new URL(param, window.location.origin);
      if (resolved.origin !== window.location.origin) return "/";
      return resolved.pathname + resolved.search + resolved.hash;
    } catch {
      return "/";
    }
  };
  const redirectTo = sanitizeRedirect(redirectParam);

  const validate = (data) => {
    let errors = {
      username: undefined,
      password: undefined,
    };

    if (!data.username) {
      errors.username = "Username is required.";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(data.username)) {
      errors.username = "Invalid email address.";
    }

    if (!data.password) {
      errors.password = "Password is required.";
    }
    return errors;
  };

  const onSubmit = async (data, form) => {
    setLoading(true);

    const userdetail = {
      UserName: data.username,
      Password: data.password,
      SessionType: 0,
    };

    const response = await httpPost(ApiEndPoints.login + "AuthMember", userdetail);
    const authData = response?.data;

    if (response?.status === httpCode.SUCCESS && authData?.AuthToken && authData?.MemberID) {
      localStorage.setItem(TOKEN_KEY, authData.AuthToken);
      localStorage.setItem(MEMBER_KEY, authData.MemberID);
      setUserRoles(authData.Roles);

      await useHttpGets(`${ApiEndPoints.login}Member/${authData.MemberID}`, undefined)
        .then(async (result) => {
          if (result.status == 200 && result.data) {
            localStorage.setItem(USER_DETAIL, JSON.stringify(result.data));

            await Promise.all([getSubscriptions(authData.MemberID), checkPermission(authData.MemberID)]);
          }
        })
        .catch(() => {
          return <ErrorMessage message="Get error while fetching users data" />;
        });

      dispatch(
        loginSuccess({
          username: data.username,
          memberId: authData.MemberID,
        })
      );

      navigate(redirectTo);
    } else if (response?.error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: response.error,
        life: 3000,
      });
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Login response was incomplete. Please try again.",
        life: 3000,
      });
    }
    form.restart();
    setLoading(false);
  };

  useEffect(() => {
    const authToken = searchParams.get("authToken");
    const memberId = searchParams.get("memberId");

    if (authToken && memberId) {
      handleAutoLogin(authToken, memberId);
    }
  }, [searchParams]);

  const getSubscriptions = async (memberId: string) => {
    const endpoint = `${ApiEndPoints.myaccount}/subscriptions?memberId=${memberId}`;
    useHttpGets(endpoint, undefined)
      .then((result) => {
        if (result.data && result.status === 200) {
          const subscriptions = Array.isArray(result.data) ? (result.data as IUserSubscription[]) : [];

          const isStarterPlan = subscriptions.some((subscription) =>
            subscription.subscriptionPlans.some((plan) => plan.planId === MONTHLY_CASUAL_PLAN)
          );
          const isCouponapplied = subscriptions.some((subscription) =>
            subscription.subscriptionPlans.some((plan) => COUPON_CODES.includes(plan.planId))
          );

          localStorage.setItem(IS_MONTHLY_CASUAL_PLAN, isStarterPlan ? "true" : "false");
          localStorage.setItem(IS_COUPONCODE_APPLIED, isCouponapplied ? "true" : "false");

          setLoading(false);
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch subscriptions", error);
        setLoading(false);
      });
  };

  const checkPermission = async (memberId: string) => {
    try {
      if (!memberId) {
        throw new Error("Member ID is required to check RP Data permission");
      }

      const endpoint = `${ApiEndPoints.subscriptions}/GetExternalSystemList?memberId=${memberId}`;

      const response = await useHttpGets(endpoint);

      if (response.error || response.status !== 200) {
        throw new Error("Error checking RP Data permission");
      }

      if (response.data) {
        localStorage.setItem("isRPDataActive", response.data.isRPDataActive ? "true" : "false");
        localStorage.setItem("isArchistarActive", response.data.isArchistarActive ? "true" : "false");
      }
    } catch (error) {
      if (toast.current) {
        toast.current.show({ severity: "error", summary: "Error", detail: "Error checking RP Data permission" });
      }
    }
  };

  const openForgotPasswordDialog = (e: MouseEvent) => {
    e.preventDefault();
    setForgotEmail("");
    setForgotEmailError("");
    setShowForgotPassword(true);
  };

  const closeForgotPasswordDialog = () => {
    setShowForgotPassword(false);
    setForgotEmail("");
    setForgotEmailError("");
    setRequestingPassword(false);
  };

  const handleRequestPassword = async () => {
    const email = forgotEmail.trim();

    if (!email) {
      setForgotEmailError("Please provide an email address.");
      return;
    }

    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      setForgotEmailError("Invalid email address.");
      return;
    }

    setForgotEmailError("");
    setRequestingPassword(true);

    const response = await httpPost(`${ApiEndPoints.changeEmail}/requestPasswordByEmail`, JSON.stringify(email));

    if (response?.status === httpCode.SUCCESS) {
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Your login credentials have been sent.",
        life: 5000,
      });
      closeForgotPasswordDialog();
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: response?.error || "Unable to request password. Please try again.",
        life: 5000,
      });
      setRequestingPassword(false);
    }
  };

  const handleAutoLogin = async (authToken: string, memberId: string) => {
    setLoader(true);

    try {
      localStorage.setItem(TOKEN_KEY, authToken);
      localStorage.setItem(MEMBER_KEY, memberId);

      const result = await useHttpGets(`${ApiEndPoints.login}Member/${memberId}`, undefined);

      if (result?.status === 200 && result.data) {
        localStorage.setItem(USER_DETAIL, JSON.stringify(result.data));
        setUserRoles((result.data as any).Roles);
        await checkPermission(memberId);

        dispatch(
          loginSuccess({
            username: (result.data as any).Email || "",
            memberId: memberId,
          })
        );

        navigate(redirectTo);
      } else {
        throw new Error("Failed to get user details");
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Auto login failed. Please login manually.",
        life: 3000,
      });
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(MEMBER_KEY);
      localStorage.removeItem(USER_DETAIL);
      localStorage.removeItem(USER_ROLES);
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="w-full flex flex-col h-screen bg-gradient-to-b from-sky-100 to-blue-100">
      <Toast ref={toast} />

      <div className="flex flex-col flex-1">
        {loader && (
          <div className="flex justify-center min-h-full pt-44">
            <REIProgressSpinner size="3rem" />
          </div>
        )}

        {!loader && (
          <div className="login h-full flex items-center justify-center py-8">
            <div className="w-full max-w-md px-4">
              <REICard className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 transform transition-all duration-300 hover:shadow-xl">
                <div className="flex justify-left animate-fadeIn">
                  <img
                    alt="logo"
                    src="https://info.realestateinvestar.com.au/hubfs/promembershiptools/images/real_estate_investar_tools.png"
                    className="mr-2 max-w-80 transform hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="border-b-2 border-[#29689d] px-4">
                  <h2 className="text-[#29689d] text-2xl font-bold text-left">MEMBERSHIP LOGIN</h2>
                  <p className="text-gray-600 text-left mt-2 text-sm">
                    If you're an existing member, please log in here.
                  </p>
                </div>

                <Form
                  onSubmit={onSubmit}
                  initialValues={{ username: "", password: "" }}
                  validate={validate}
                  render={({ handleSubmit, submitting, valid }) => (
                    <form onSubmit={handleSubmit} className="p-fluid px-3">
                      <Field
                        name="username"
                        render={({ input, meta }) => (
                          <div className="mb-4 w-full">
                            <InputField
                              id="username"
                              testId="login-txt-username"
                              input={input}
                              meta={meta}
                              placeholder="Email Address"
                              icon="pi-envelope"
                              className="shadow-sm"
                            />
                          </div>
                        )}
                      />
                      <Field
                        name="password"
                        render={({ input, meta }) => (
                          <div className="mb-2 w-full">
                            <InputField
                              id="password"
                              testId="login-txt-password"
                              input={input}
                              meta={meta}
                              placeholder="Password"
                              type="password"
                              icon="pi-lock"
                              className="shadow-sm"
                            />
                          </div>
                        )}
                      />

                      <div className="text-right mb-5">
                        <a
                          href="#"
                          id="login-lnk-forgotpassword"
                          data-testid="login-lnk-forgotpassword"
                          onClick={openForgotPasswordDialog}
                          className="text-[#29689d] hover:text-[#1e4e77] text-sm transition-colors duration-200"
                        >
                          Forgot password?
                        </a>
                      </div>

                      <div className="mt-4">
                        <Button
                          label="SIGN IN"
                          id="login-btn-signin"
                          data-testid="login-btn-signin"
                          type="submit"
                          disabled={submitting || !valid}
                          autoFocus
                          loading={loading}
                          className={`w-full py-3 font-semibold text-white ${
                            !submitting && valid
                              ? "bg-[#29689d] hover:bg-[#1e4e77] border-[#29689d]"
                              : "bg-gray-400 border-gray-400"
                          } transition-colors duration-200 rounded-md shadow-md hover:shadow-lg`}
                        />
                      </div>
                    </form>
                  )}
                />

                <div className="mt-6 pt-5 border-t border-gray-200 px-4">
                  <div className="flex flex-col gap-3 text-sm text-gray-600">
                    <div className="text-left">
                      Don't have a membership?{" "}
                      <REILinkButton
                        href="https://info.realestateinvestar.com.au/pricing"
                        target="_blank"
                        rel="noopener noreferrer"
                        id="login-lnk-signup"
                        data-testid="login-lnk-signup"
                        className="text-[#29689d] hover:text-[#1e4e77] font-medium transition-colors duration-200"
                      >
                        Sign Up Here
                      </REILinkButton>
                    </div>

                    <div className="text-left text-xs text-gray-500">
                      Having trouble logging in? Cookies must be enabled to use our website,{" "}
                      <REILinkButton
                        href="https://www.wikihow.com/Enable-Cookies-in-Your-Internet-Web-Browser"
                        target="_blank"
                        rel="noopener noreferrer"
                        id="login-lnk-cookies"
                        data-testid="login-lnk-cookies"
                        className="text-[#29689d] hover:underline transition-colors duration-200"
                      >
                        click here
                      </REILinkButton>{" "}
                      for instructions.
                    </div>

                    <div className="text-left text-xs text-gray-500">
                      By logging into this site, I have read and agree with the
                      <a
                        href="https://info.realestateinvestar.com.au/terms-conditions-v6"
                        target="_blank"
                        id="login-lnk-terms"
                        data-testid="login-lnk-terms"
                        className="text-[#29689d] hover:underline transition-colors duration-200"
                      >
                        {" "}
                        Terms & Conditions
                      </a>
                    </div>
                  </div>
                </div>
              </REICard>
            </div>
          </div>
        )}
      </div>

      <Dialog
        header="Request Your Password"
        visible={showForgotPassword}
        style={{ width: "90vw", maxWidth: "520px" }}
        onHide={closeForgotPasswordDialog}
        draggable={false}
        footer={
          <div className="flex justify-end">
            <Button
              label="Close"
              id="login-btn-forgotclose"
              data-testid="login-btn-forgotclose"
              onClick={closeForgotPasswordDialog}
              className="bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200"
            />
          </div>
        }
      >
        <div className="flex flex-col gap-3 pt-1">
          <p className="text-sm text-gray-700 m-0">
            If you forgot your password, please enter your email address here and we will email it to you.
          </p>
          <p className="text-sm text-gray-700 m-0">Enter the e-mail address you used when you registered</p>

          <div className="p-inputgroup flex-1 mt-1">
            <span className="p-inputgroup-addon">
              <i className="pi pi-envelope" />
            </span>
            <InputText
              id="forgotEmail"
              data-testid="login-txt-forgotemail"
              value={forgotEmail}
              onChange={(e) => {
                setForgotEmail(e.target.value);
                if (forgotEmailError) setForgotEmailError("");
              }}
              placeholder="Enter email address"
              className={forgotEmailError ? "p-invalid w-full" : "w-full"}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleRequestPassword();
                }
              }}
            />
          </div>
          {forgotEmailError && <small className="p-error">{forgotEmailError}</small>}

          <div>
            <Button
              label="Request password"
              id="login-btn-requestpassword"
              data-testid="login-btn-requestpassword"
              onClick={handleRequestPassword}
              loading={requestingPassword}
              disabled={requestingPassword}
              className="bg-[#007bff] border-[#007bff] hover:bg-[#0069d9] text-white"
            />
          </div>
        </div>
      </Dialog>

      <div className="p-3 text-left text-xs text-gray-500 bg-white bg-opacity-50">
        © {new Date().getFullYear()} Real Estate Investar. All rights reserved.
      </div>
    </div>
  );
};

export default Login;
